import { useState, useEffect } from "react";
import type { BackendOrder, TabType } from "../components/Tracking/types";
import { getOrdersByStageV2 } from "./useSupabase";

export interface UseTrackingProps {
  searchTerm: string;
}

export interface UseTrackingReturn {
  orders: BackendOrder[];
  loading: boolean;
  refetch: () => void;
}

export const useTracking = ({ 
  searchTerm
}: UseTrackingProps): UseTrackingReturn => {
  const [allOrders, setAllOrders] = useState<BackendOrder[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch orders from the database
  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await getOrdersByStageV2({
        limit: 1000, // Get all orders for frontend filtering and pagination
        offset: 0,
        ...(searchTerm && { search: searchTerm }),
      });
      
      const typedResponse = response as any;
      if (typedResponse?.status === 'success') {
        setAllOrders(typedResponse.data?.orders ?? []);
      } else {
        setAllOrders([]);
      }
    } catch (error) {
      setAllOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [searchTerm]);

  return {
    orders: allOrders, // Return all orders so frontend filtering works
    loading,
    refetch: fetchOrders,
  };
};

// Utility functions for filtering and counting orders
export const filterOrdersByTab = (
  orders: BackendOrder[],
  activeTab: TabType,
  searchTerm: string
): BackendOrder[] => {
  return orders.filter((order) => {
    const orderNumber = order.order_number || order.id?.toString() || "";
    const customerName = order.customer_name || "Unknown Customer";
    const matchesSearch =
      orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customerName.toLowerCase().includes(searchTerm.toLowerCase());

    const stageName = order.stage_name?.toLowerCase() || "";
    let matchesTab = false;
    if (activeTab === "active") {
      matchesTab = ["pending", "confirmed", "processing", "on hold"].includes(stageName);
    } else if (activeTab === "shipped") {
      matchesTab = stageName === "shipped";
    } else if (activeTab === "delivered") {
      matchesTab = stageName === "delivered" || stageName === "completed";
    } else if (activeTab === "cancelled") {
      matchesTab = ["cancelled", "returned", "refunded"].includes(stageName);
    }
    
    return matchesTab && matchesSearch;
  });
};

export const getOrderCountByTab = (orders: BackendOrder[], tab: TabType): number => {
  return orders.filter((order) => {
    const stageName = order.stage_name?.toLowerCase() || "";
    if (tab === "active") {
      return ["pending", "confirmed", "processing", "on hold"].includes(stageName);
    } else if (tab === "shipped") {
      return stageName === "shipped";
    } else if (tab === "delivered") {
      return stageName === "delivered" || stageName === "completed";
    } else if (tab === "cancelled") {
      return ["cancelled", "returned", "refunded"].includes(stageName);
    }
    return false;
  }).length;
};
